import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireSessionUser } from '@/integrations/supabase/session';
import { sanitizeSearchQuery } from '@/utils/sanitize';
import { toast } from '@/hooks/use-toast';
import type { JobWithRelations } from '@/types';
import type { JobFinancials, JobPayment } from '@/components/jobs/CompleteJobDialog';
import { formatDateInputInTz, DEFAULT_TIMEZONE, pickedDayKey, pickedDayStartInTz } from '@/utils/timezone';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useVisibility } from '@/hooks/useVisibility';

// Wraps raw Supabase PostgrestError into a proper JS Error for Sentry
function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const pgErr = err as { message: string; code?: string; details?: string; hint?: string };
    const e = new Error(`[DB ${pgErr.code ?? 'error'}] ${pgErr.message}`);
    e.name = 'PostgrestError';
    (e as any).details = pgErr.details;
    (e as any).hint = pgErr.hint;
    (e as any).code = pgErr.code;
    return e;
  }
  return new Error(String(err));
}

const JOB_SELECT = `
          *,
          service_types(name),
          lead_sources(name),
          techs(id, name)
        ` as const;

const JOB_SELECT_WITH_TECH_PAY = `
          *,
          service_types(name),
          lead_sources(name),
          techs(id, name, commission_percentage)
        ` as const;

// Helper function to trigger job closed webhook (non-blocking but with logging)
async function triggerJobClosedWebhook(jobId: string, companyId: string) {
  console.log(`🚀 [CTM Webhook] Triggering webhook for job ${jobId}, company ${companyId}`);

  try {
    const { data, error } = await supabase.functions.invoke('send-job-closed-webhook', {
      body: { jobId, companyId },
    });

    if (error) {
      console.error('❌ [CTM Webhook] Job closed webhook error:', error);
      // Show toast to user so they know webhook failed
      toast({
        title: 'Webhook notification failed',
        description: 'Job completed successfully but external notification failed. Please check system logs.',
        variant: 'destructive',
      });
    } else {
      console.log('✅ [CTM Webhook] Webhook sent successfully:', data);
    }
  } catch (err) {
    // Don't throw - webhook failure shouldn't affect job completion
    console.error('❌ [CTM Webhook] Failed to trigger job closed webhook:', err);
    toast({
      title: 'Webhook notification error',
      description: 'Job completed successfully but external notification encountered an error.',
      variant: 'destructive',
    });
  }
}


export function useJobs(
  statusFilter?: string,
  searchQuery?: string,
  dateFrom?: Date,
  dateTo?: Date,
  timezone: string = DEFAULT_TIMEZONE
) {
  // Realtime subscriptions now handled by RealtimeProvider
  const { canSeeTechPay } = useVisibility();

  return useQuery({
    queryKey: ['jobs', statusFilter, searchQuery, dateFrom, dateTo, timezone, canSeeTechPay],
    queryFn: async () => {
      let query = supabase
        .from('jobs')
        .select(canSeeTechPay ? JOB_SELECT_WITH_TECH_PAY : JOB_SELECT)
        .order('created_at', { ascending: false });

      // Board grouping (jobBoardStatus): en-route scheduled jobs count as Ongoing.
      if (statusFilter === 'in_progress') {
        query = query.or('status.eq.in_progress,and(status.eq.scheduled,en_route_at.not.is.null)');
      } else if (statusFilter === 'scheduled') {
        query = query.eq('status', 'scheduled').is('en_route_at', null);
      } else if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Server-side date filtering. Ongoing is live work, so it ignores the range.
      if ((dateFrom || dateTo) && statusFilter !== 'in_progress') {
        const dateField = (statusFilter === 'completed' || statusFilter === 'cancelled') ? 'job_date'
          : statusFilter === 'scheduled' ? 'scheduled_date'
            : 'created_at';

        // For completed/cancelled jobs, filter by job_date (editable business date)
        if (statusFilter === 'completed' || statusFilter === 'cancelled') {
          const fromDate = dateFrom ? formatDateInputInTz(dateFrom, timezone) : undefined;
          const toDate = dateTo ? formatDateInputInTz(dateTo, timezone) : undefined;

          if (fromDate) query = query.gte('job_date', fromDate);
          if (toDate) query = query.lte('job_date', toDate);
        } else {
          if (dateFrom) {
            query = query.gte(dateField, dateFrom.toISOString());
          }
          if (dateTo) {
            query = query.lte(dateField, dateTo.toISOString());
          }
        }
      }

      if (searchQuery) {
        const safe = sanitizeSearchQuery(searchQuery);
        if (safe.length > 0) {
          query = query
            .or(`customer_name.ilike.%${safe}%,customer_phone.ilike.%${safe}%,job_number.ilike.%${safe}%`)
            .limit(50);
        }
      }

      const { data, error } = await query;
      if (error) throw toError(error);

      if (!data) return [];

      // Fetch CSR profiles in bulk (optimized - single query)
      const csrIds = [...new Set(data.filter(job => job.assigned_csr_id).map(job => job.assigned_csr_id))];

      const csrProfiles = new Map();
      if (csrIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', csrIds);

        profiles?.forEach(profile => {
          csrProfiles.set(profile.id, profile);
        });
      }

      // Attach CSR profiles to jobs
      return data.map(job => ({
        ...job,
        assigned_csr: job.assigned_csr_id ? csrProfiles.get(job.assigned_csr_id) : null
      })) as JobWithRelations[];
    },
    staleTime: 30000, // Data stays fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: keepPreviousData,
  });
}

export function useJob(jobId: string | null) {
  const { canSeeTechPay } = useVisibility();
  return useQuery({
    queryKey: ['job', jobId, canSeeTechPay],
    queryFn: async () => {
      if (!jobId) return null;

      const { data, error } = await supabase
        .from('jobs')
        .select(canSeeTechPay ? JOB_SELECT_WITH_TECH_PAY : JOB_SELECT)
        .eq('id', jobId)
        .single();

      if (error) throw toError(error);

      // Fetch CSR profile
      let csrProfile = null;
      if (data.assigned_csr_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', data.assigned_csr_id)
          .maybeSingle();
        csrProfile = profile;
      }

      return { ...data, csr_profile: csrProfile } as JobWithRelations;
    },
    enabled: !!jobId,
  });
}

export function useUpdateJobStatus() {
  const queryClient = useQueryClient();
  const { timezone } = useTimezoneUtils();

  return useMutation({
    mutationFn: async ({
      jobId,
      status,
      cancellationReason,
      closeDate
    }: {
      jobId: string;
      status: string;
      cancellationReason?: string;
      closeDate?: Date;
    }) => {
      const updateData: any = { status };

      // closeDate is a calendar pick (its local fields are the day); without one it's today
      // in the company timezone.
      const jobDate = closeDate ? pickedDayKey(closeDate) : formatDateInputInTz(new Date(), timezone);
      if (status === 'completed') {
        updateData.closed_at = new Date().toISOString();
        updateData.job_date = jobDate;
      } else if (status === 'cancelled') {
        updateData.cancelled_at = new Date().toISOString();
        updateData.job_date = jobDate;
        if (cancellationReason) {
          updateData.cancellation_reason = cancellationReason;
        }
      }

      const { error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Job status updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobTechnician() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      technicianId,
    }: {
      jobId: string;
      technicianId: string | null;
      /** Same technician as before: the office is sending the job to them again. */
      resend?: boolean;
      techName?: string | null;
    }) => {
      // office_assign_tech (20260922100000): a different tech resets the acknowledgement through
      // the trigger; the SAME tech is asked again (pending + tech_ack_reset_at) unless on site.
      const { error } = await supabase.rpc('office_assign_tech', { p_job_id: jobId, p_tech_id: technicianId });
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-audit-log', variables.jobId] });
      const who = variables.techName ? ` ${variables.techName}` : ' the technician';
      toast({
        title: variables.technicianId === null
          ? 'Technician unassigned'
          : variables.resend
            ? `Sent to${who} again`
            : `Sent to${who}`,
        description: variables.technicianId === null
          ? undefined
          : 'They will be asked to accept it in the Steddy app.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update technician',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobRecallFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      isRecall
    }: {
      jobId: string;
      isRecall: boolean;
    }) => {
      const { error } = await supabase
        .from('jobs')
        .update({ is_recall: isRecall })
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['technician-performance'] });
      toast({ title: variables.isRecall ? 'Job marked as recall' : 'Recall flag removed' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update recall flag',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobFinancials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      invoice_total,
      total_parts_cost,
      toll_cost
    }: {
      jobId: string;
      invoice_total?: number;
      total_parts_cost?: number;
      toll_cost?: number;
    }) => {
      const updateData: any = {};
      if (invoice_total !== undefined) updateData.invoice_total = invoice_total;
      if (total_parts_cost !== undefined) updateData.total_parts_cost = total_parts_cost;
      if (toll_cost !== undefined) updateData.toll_cost = toll_cost;

      const { error } = await supabase
        .from('jobs')
        .update(updateData)
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Job financials updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update financials',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Turns the close_job RPC's error text into something the office can act on.
 * STATE_CONFLICT:  the job changed elsewhere (closed from the app, cancelled, ...).
 * PARTS_EXCEED_TOTAL: parts charged to the customer add up to more than the job total.
 */
function closeJobErrorMessage(error: { message?: string } | Error | null | undefined): string {
  const raw = error?.message ?? 'Unknown error';
  if (raw.includes('STATE_CONFLICT')) {
    const detail = raw.replace(/^.*STATE_CONFLICT:\s*/, '').trim();
    return `This job changed elsewhere (${detail || 'possibly closed from the Steddy app'}). It has been reloaded; check its status before trying again.`;
  }
  if (raw.includes('PARTS_EXCEED_TOTAL')) {
    return 'The parts charged to the customer add up to more than the job total. Lower a part sell price or raise the total.';
  }
  if (raw.includes('scheduled in the future')) {
    return 'Cannot complete (post) a job scheduled in the future. Please update the schedule date first.';
  }
  return raw;
}

/**
 * Close & Post. Runs the close_job RPC (20260912100900 + 20260916110000), the same path
 * the Steddy app uses: one transaction, per-company advisory lock, idempotent for a job
 * already closed with a live invoice, one live invoice per job enforced by
 * invoices_one_live_per_job. The RPC's "legacy" input mode takes exactly what the wizard
 * collects (gross total, parts cost, tolls, summary, payments) and builds the invoice
 * lines the way buildInvoiceLineItems does (labour = total − parts at sell price, one
 * 'Parts - <name>' line per part). buildInvoiceLineItems stays in use for the preview only.
 */
export function useCompleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, financials, payments }: {
      jobId: string;
      financials: JobFinancials;
      payments: JobPayment[];
      /** Kept for the dialog's API; close_job stamps closed_at/job_date with the server clock. */
      closeDate: Date;
    }) => {
      const { data: invoiceId, error } = await supabase.rpc('close_job', {
        p_job_id: jobId,
        p_line_items: null,
        p_prices_include_tax: null,
        p_invoice_total: financials.invoice_total,
        p_total_parts_cost: financials.total_parts_cost,
        p_toll_cost: financials.toll_cost,
        p_work_summary: financials.work_summary?.trim() || null,
        p_payments: payments.map((p) => ({
          payment_method: p.payment_method,
          amount: p.amount,
          payment_date: p.payment_date.toISOString(),
          notes: p.notes || null,
        })),
        p_signature: null,
      });

      if (error) {
        const wrapped = toError(error);
        wrapped.message = closeJobErrorMessage(error);
        throw wrapped;
      }
      return invoiceId as string | null;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-payments', variables.jobId] });
      // Invoice invalidations
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['job-invoice', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });

      // Trigger webhook asynchronously (non-blocking)
      (async () => {
        try {
          // Get company_id from job data - we need to fetch it
          console.log(`📋 [CTM Webhook] Job completed, fetching company_id for job ${variables.jobId}`);

          const { data, error } = await supabase
            .from('jobs')
            .select('company_id')
            .eq('id', variables.jobId)
            .single();

          if (error) {
            console.error('❌ [CTM Webhook] Failed to fetch company_id:', error);
            return;
          }

          if (data?.company_id) {
            console.log(`✅ [CTM Webhook] Company ID found: ${data.company_id}, triggering events...`);

            // Trigger webhook (includes both company webhook and CTM sale update)
            await triggerJobClosedWebhook(variables.jobId, data.company_id);
          } else {
            console.warn('⚠️ [CTM Webhook] No company_id found for job');
          }
        } catch (err) {
          console.error('❌ [CTM Webhook] Exception fetching company_id:', err);
        }
      })();


      toast({
        title: 'Job completed successfully',
        description: 'Job has been marked as completed and an invoice has been generated.',
      });
    },
    onError: (error: Error, variables) => {
      // A conflict means the row moved on (often closed from the app): show the truth.
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast({
        title: 'Failed to complete job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      // First, get the job to check for linked lead
      const { data: job, error: fetchError } = await supabase
        .from('jobs')
        .select('lead_id')
        .eq('id', jobId)
        .single();

      if (fetchError) throw fetchError;

      // If there's a linked lead, update its status back to new
      if (job?.lead_id) {
        const { error: leadError } = await supabase
          .from('leads')
          .update({
            status: 'new',
            job_id: null,
            converted_at: null
          })
          .eq('id', job.lead_id);

        if (leadError) {
          console.error('Error reverting lead status:', leadError);
          // We continue with job deletion even if lead update fails, 
          // but we log it. Ideally this would be a transaction.
        }
      }



      // Now delete the job
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);

      if (error) throw toError(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] }); // Also invalidate leads
      toast({
        title: 'Job deleted',
        description: 'Job has been permanently deleted and linked lead reverted to new.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobCloseDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, closeDate }: { jobId: string; closeDate: Date }) => {
      const { error } = await supabase
        .from('jobs')
        .update({ job_date: pickedDayKey(closeDate) })
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Job date updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update close date',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobScheduledDate() {
  const queryClient = useQueryClient();
  const { timezone } = useTimezoneUtils();

  return useMutation({
    mutationFn: async ({
      jobId,
      scheduledDate,
      scheduledTimeStart,
      scheduledTimeEnd
    }: {
      jobId: string;
      scheduledDate: Date;
      scheduledTimeStart?: string | null;
      scheduledTimeEnd?: string | null;
    }) => {
      const { error } = await supabase
        .from('jobs')
        .update({
          scheduled_date: pickedDayStartInTz(scheduledDate, timezone).toISOString(),
          scheduled_time_start: scheduledTimeStart || null,
          scheduled_time_end: scheduledTimeEnd || null,
        })
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Job rescheduled successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reschedule job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateJobServiceType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      serviceTypeId
    }: {
      jobId: string;
      serviceTypeId: string | null;
    }) => {
      const { error } = await supabase
        .from('jobs')
        .update({ service_type_id: serviceTypeId })
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Service type updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update service type',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** Thrown by useUpdateJobNotes when the row's notes moved on since the editor opened. */
export class NotesConflictError extends Error {
  constructor(public readonly conflictNotes: string | null) {
    super('Notes changed while you were editing (a technician may have added a line from the app). The latest notes have been reloaded; please re-apply your change.');
    this.name = 'NotesConflictError';
  }
}

/**
 * Full overwrite of jobs.notes, guarded: the tech app appends lines through
 * tech_append_job_note, so a blind overwrite would silently drop them. When
 * expectedNotes is given, the save is refused unless the row still matches it.
 */
export function useUpdateJobNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, notes, expectedNotes }: { jobId: string; notes: string; expectedNotes?: string | null }) => {
      if (expectedNotes !== undefined) {
        const { data: current, error: readError } = await supabase
          .from('jobs')
          .select('notes')
          .eq('id', jobId)
          .single();
        if (readError) throw toError(readError);
        if ((current?.notes ?? '') !== (expectedNotes ?? '')) {
          throw new NotesConflictError(current?.notes ?? null);
        }
      }
      const { error } = await supabase
        .from('jobs')
        .update({ notes: notes.trim() || null })
        .eq('id', jobId);
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      toast({ title: 'Notes saved' });
    },
    onError: (error: Error, variables) => {
      if (error instanceof NotesConflictError) {
        queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      }
      toast({
        title: error instanceof NotesConflictError ? 'Notes changed elsewhere' : 'Failed to save notes',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * Atomic append through tech_append_job_note (the RPC the Steddy app uses; staff of the
 * tenant may call it too). The server stamps "[DD/MM HH:MM Author] text" in the company
 * timezone, so office and field notes interleave without clobbering each other.
 */
export function useAppendJobNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, text }: { jobId: string; text: string }) => {
      const body = text.trim();
      if (!body) throw new Error('Note is empty');
      const { error } = await supabase.rpc('tech_append_job_note', { p_job_id: jobId, p_text: body });
      if (error) throw toError(error);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-audit-log', variables.jobId] });
      toast({ title: 'Note added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add note',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
